import React, { useMemo } from 'react';
import getOperationalBasePrices, {
  type OperationalBaseAgencyOption,
} from '~/utils/operational-base-prices';
import styles from './phase-header-rates.module.scss';

export type PhaseHeaderRatesProps = {
  logistics: {
    operationalBase?: string;
    calculatedPriceUsed?: boolean;
    discountMO?: number;
  };
  transport: {
    discount?: number;
    trucks?: {
      maxSize?: string;
      type?: string;
    };
  };
  hourlyRate?: {
    price?: number;
  } | null;
  agencyId?: string;
  agencies?: OperationalBaseAgencyOption[];
};

type RateLineProps = {
  label: string;
  baseValue: number;
  discount?: number;
};

const RateLine = ({ label, baseValue, discount = 0 }: RateLineProps) => {
  const hasDiscount = discount > 0;
  const discounted = hasDiscount ? baseValue * (1 - discount) : null;

  return (
    <p className={`${styles.rate} ${hasDiscount ? styles.discount : ''}`}>
      {label} : <span>{baseValue.toFixed(2)} €</span>
      {discounted != null && <span>{discounted.toFixed(2)} €</span>}
    </p>
  );
};

const PhaseHeaderRates = ({
  logistics,
  transport,
  hourlyRate,
  agencyId,
  agencies,
}: PhaseHeaderRatesProps) => {
  const operationalBasePrice = useMemo(
    () => getOperationalBasePrices(
      logistics?.operationalBase,
      agencyId,
      agencies,
      transport?.trucks,
    ),
    [agencies, agencyId, logistics?.operationalBase, transport?.trucks],
  );

  const usedHourlyRate = useMemo(() => {
    if (hourlyRate?.price && logistics?.calculatedPriceUsed === true) {
      return hourlyRate.price;
    }
    return operationalBasePrice.hourlyRate ?? null;
  }, [hourlyRate?.price, logistics?.calculatedPriceUsed, operationalBasePrice.hourlyRate]);

  const transportDiscount = transport?.discount ?? 0;
  const workforceDiscount = logistics?.discountMO ?? 0;

  const hasTransportRates = operationalBasePrice.truckT7 != null
    || operationalBasePrice.truck6m != null
    || operationalBasePrice.truck12m != null;

  if (usedHourlyRate == null && !hasTransportRates) {
    return null;
  }

  return (
    <div className={styles.root}>
      {usedHourlyRate != null && (
        <RateLine
          label="Taux horaire de Main d'œuvre utilisé"
          baseValue={usedHourlyRate}
          discount={workforceDiscount}
        />
      )}
      {operationalBasePrice.truckT7 != null && (
        <RateLine
          label="Taux transport T7"
          baseValue={operationalBasePrice.truckT7}
          discount={transportDiscount}
        />
      )}
      {operationalBasePrice.truck6m != null && (
        <RateLine
          label="Taux transport 6m"
          baseValue={operationalBasePrice.truck6m}
          discount={transportDiscount}
        />
      )}
      {operationalBasePrice.truck12m != null && (
        <RateLine
          label="Taux transport 12m"
          baseValue={operationalBasePrice.truck12m}
          discount={transportDiscount}
        />
      )}
    </div>
  );
};

export default PhaseHeaderRates;
